import React, { useState } from 'react';
import { userApi } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { PlusIcon } from 'components/icons';
import toast from 'react-hot-toast';

interface MatchScoreHintsProps {
  opportunity: {
    setAside?: string;
    typeOfSetAside?: string;
    naicsCodes?: string[];
    placeOfPerformance?: { state?: string };
  };
  user: {
    certifications?: string[];
    naicsCodes?: string[];
    companyState?: string;
  };
  onProfileUpdated?: () => void;
}

const SET_ASIDE_CERT_MAP: Record<string, string> = {
  'SBA': '8(a)',
  '8A': '8(a)',
  '8(a)': '8(a)',
  '8AN': '8(a)',
  'WOSB': 'WOSB',
  'WOSBSS': 'WOSB',
  'EDWOSB': 'EDWOSB',
  'EDWOSBSS': 'EDWOSB',
  'SDVOSBC': 'SDVOSB',
  'SDVOSBS': 'SDVOSB',
  'SDVOSB': 'SDVOSB',
  'HZC': 'HUBZone',
  'HZS': 'HUBZone',
  'HUBZONE': 'HUBZone',
  'VSA': 'SDVOSB',
  'VSS': 'SDVOSB',
};

const MatchScoreHints: React.FC<MatchScoreHintsProps> = ({ opportunity, user, onProfileUpdated }) => {
  const { refreshUser } = useAuth();
  const [saving, setSaving] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const hints: { id: string; text: string; action: string; onConfirm: () => Promise<void>; onDeny: () => void }[] = [];

  // Certification hint
  const oppSetAside = opportunity.typeOfSetAside || opportunity.setAside;
  if (oppSetAside) {
    const requiredCert = SET_ASIDE_CERT_MAP[oppSetAside];
    if (requiredCert && !user.certifications?.includes(requiredCert)) {
      hints.push({
        id: `cert_${requiredCert}`,
        text: `This is a ${oppSetAside} set-aside. Do you have ${requiredCert} certification?`,
        action: `Yes, add ${requiredCert}`,
        onConfirm: async () => {
          setSaving(`cert_${requiredCert}`);
          try {
            await userApi.updateMe({
              certifications: [...(user.certifications || []), requiredCert],
            });
            await refreshUser();
            toast.success(`${requiredCert} certification added`);
            onProfileUpdated?.();
          } finally { setSaving(null); }
        },
        onDeny: () => setDismissed(prev => new Set(prev).add(`cert_${requiredCert}`)),
      });
    }
  }

  // NAICS hint
  const oppNaics = opportunity.naicsCodes || [];
  const userNaics = user.naicsCodes || [];
  const missingNaics = oppNaics.filter(code => !userNaics.includes(code));
  if (missingNaics.length > 0 && missingNaics.length <= 2) {
    for (const code of missingNaics.slice(0, 1)) {
      hints.push({
        id: `naics_${code}`,
        text: `This opportunity uses NAICS ${code}. Is this one of your codes?`,
        action: 'Yes, add it',
        onConfirm: async () => {
          setSaving(`naics_${code}`);
          try {
            await userApi.updateMe({
              naicsCodes: [...userNaics, code],
            });
            await refreshUser();
            toast.success(`NAICS ${code} added to your profile`);
            onProfileUpdated?.();
          } finally { setSaving(null); }
        },
        onDeny: () => setDismissed(prev => new Set(prev).add(`naics_${code}`)),
      });
    }
  }

  // State hint
  const oppState = (opportunity.placeOfPerformance as any)?.state;
  if (oppState && !user.companyState) {
    hints.push({
      id: `state_${oppState}`,
      text: `This work is in ${oppState}. Is your company located nearby?`,
      action: `Set my state to ${oppState}`,
      onConfirm: async () => {
        setSaving(`state_${oppState}`);
        try {
          await userApi.updateMe({ companyState: oppState });
          await refreshUser();
          toast.success(`Company state set to ${oppState}`);
          onProfileUpdated?.();
        } finally { setSaving(null); }
      },
      onDeny: () => setDismissed(prev => new Set(prev).add(`state_${oppState}`)),
    });
  }

  // Filter out dismissed hints
  const activeHints = hints.filter(h => !dismissed.has(h.id));

  if (activeHints.length === 0) return null;

  return (
    <div className="space-y-2">
      <p className="text-xs font-medium text-content-muted uppercase tracking-wide">Improve Your Score</p>
      {activeHints.map(hint => (
        <div key={hint.id} className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-content flex-1">{hint.text}</p>
          <div className="flex items-center gap-2 ml-3 flex-shrink-0">
            <button
              onClick={hint.onConfirm}
              disabled={saving === hint.id}
              className="inline-flex items-center gap-1 px-2.5 py-1 bg-brand-600 text-white text-xs font-medium rounded-lg hover:bg-brand-500 disabled:opacity-50"
            >
              {saving === hint.id ? (
                'Saving...'
              ) : (
                <><PlusIcon className="h-3 w-3" />{hint.action}</>
              )}
            </button>
            <button
              onClick={hint.onDeny}
              className="text-xs text-content-subtle hover:text-content-muted"
            >
              No
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default MatchScoreHints;
